import { AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui";

/**
 * GlobalErrorScreen
 *
 * The graceful fallback shown when something fails catastrophically — either
 * a render crash caught by {@link ErrorBoundary} or a route error surfaced by
 * the router (via {@link RouteError}). Presentational only; callers supply the
 * error and the recovery action.
 */
export interface GlobalErrorScreenProps {
  error?: unknown;
  onReset?: () => void;
  resetLabel?: string;
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "An unexpected error occurred.";
}

export function GlobalErrorScreen({
  error,
  onReset,
  resetLabel = "Reload",
}: GlobalErrorScreenProps) {
  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center gap-5 bg-bg-base px-6 text-center">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-md border border-border-strong text-text-primary"
        aria-hidden
      >
        <AlertOctagon size={20} strokeWidth={1.5} />
      </div>

      <div className="flex max-w-md flex-col gap-2">
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          Something went wrong
        </h1>
        <p className="text-md text-text-secondary">
          The application hit an unexpected error. Your data is untouched — you
          can safely reload.
        </p>
      </div>

      <pre className="selectable max-w-md overflow-x-auto rounded-md border border-border bg-bg-elevated px-3 py-2 text-left font-mono text-sm text-text-tertiary">
        {describe(error)}
      </pre>

      {onReset && (
        <Button variant="primary" onClick={onReset}>
          {resetLabel}
        </Button>
      )}
    </div>
  );
}
